import React, { useCallback, useRef, useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Textarea,
  Switch,
  Dropdown,
  Option,
  Divider,
  Label,
} from '@fluentui/react-components';
import SidePanel from './shared/SidePanel';
import PalettePicker from './edit/PalettePicker';
import type { PaletteOption } from './edit/visualConfig/types';
import type { UpdateDashboardSettingsRequest } from '../api/types';

// ── Types ──────────────────────────────────────────────────────────────────

interface DashboardSettingsValues {
  theme: string;
  animations: boolean;
  layout: {
    width: 'centered' | 'full';
    maxWidth: number;
  };
  description: string;
}

interface DashboardSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  settings: DashboardSettingsValues;
  dashboardName: string;
  dashboardDescription: string;
  onSettingsChange: (partial: UpdateDashboardSettingsRequest) => void;
  /** Called when name or description changes (debounced by this component). */
  onDashboardMetaChange: (updates: { name?: string; description?: string }) => void;
}

// ── Defaults ───────────────────────────────────────────────────────────────

export const DEFAULT_DASHBOARD_SETTINGS: DashboardSettingsValues = {
  theme: 'default',
  animations: true,
  layout: {
    width: 'centered',
    maxWidth: 1600,
  },
  description: '',
};

/** Merge stored (possibly partial) settings with defaults */
export function mergeDashboardSettings(
  stored: Partial<DashboardSettingsValues> | null | undefined,
): DashboardSettingsValues {
  const merged = { ...DEFAULT_DASHBOARD_SETTINGS };
  if (!stored) return merged;
  if (stored.theme !== undefined) merged.theme = stored.theme;
  if (stored.animations !== undefined) merged.animations = stored.animations;
  if (stored.layout) {
    merged.layout = { ...merged.layout, ...stored.layout };
  }
  return merged;
}

// ── Styles ─────────────────────────────────────────────────────────────────

const PANEL_WIDTH = 320;

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
});

// ── Component ──────────────────────────────────────────────────────────────

const DashboardSettingsPanel: React.FC<DashboardSettingsPanelProps> = ({
  isOpen,
  onClose,
  settings,
  dashboardName,
  dashboardDescription,
  onSettingsChange,
  onDashboardMetaChange,
}) => {
  const styles = useStyles();
  const metaDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Local state for name/description so typing is immediate
  const [localName, setLocalName] = useState(dashboardName);
  const [localDescription, setLocalDescription] = useState(dashboardDescription);

  // Sync local state when parent value changes (e.g. after API response or inline title edit)
  useEffect(() => { setLocalName(dashboardName); }, [dashboardName]);
  useEffect(() => { setLocalDescription(dashboardDescription); }, [dashboardDescription]);

  // Debounced change dispatcher for name/description
  const dispatchMetaChange = useCallback(
    (updates: { name?: string; description?: string }) => {
      if (metaDebounceRef.current) clearTimeout(metaDebounceRef.current);
      metaDebounceRef.current = setTimeout(() => {
        onDashboardMetaChange(updates);
      }, 300);
    },
    [onDashboardMetaChange],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (metaDebounceRef.current) clearTimeout(metaDebounceRef.current);
    };
  }, []);

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} title="Dashboard Settings" width={PANEL_WIDTH}>
        {/* General */}
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>General</Text>

          <div className={styles.field}>
            <Label size="small">Name</Label>
            <Input
              size="small"
              value={localName}
              onChange={(_, data) => {
                setLocalName(data.value);
                dispatchMetaChange({ name: data.value });
              }}
              appearance="underline"
            />
          </div>

          <div className={styles.field}>
            <Label size="small">Description</Label>
            <Textarea
              size="small"
              value={localDescription}
              onChange={(_, data) => {
                setLocalDescription(data.value);
                dispatchMetaChange({ description: data.value });
              }}
              placeholder="Add a description..."
              resize="vertical"
              rows={3}
              appearance="outline"
            />
          </div>
        </div>

        <Divider />

        {/* Appearance */}
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>Appearance</Text>

          <div className={styles.field}>
            <Label size="small">Color theme</Label>
            <PalettePicker
              value={settings.theme as PaletteOption}
              onChange={(palette) => onSettingsChange({ theme: palette })}
            />
          </div>

          <div className={styles.fieldRow}>
            <Label size="small">Animations</Label>
            <Switch
              checked={settings.animations}
              onChange={(_, data) => {
                onSettingsChange({ animations: data.checked });
              }}
            />
          </div>
        </div>

        <Divider />

        {/* Layout */}
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>Layout</Text>

          <div className={styles.field}>
            <Label size="small">Width mode</Label>
            <Dropdown
              size="small"
              value={settings.layout.width === 'centered' ? 'Centered (1600px)' : 'Full width'}
              selectedOptions={[settings.layout.width]}
              onOptionSelect={(_, data) => {
                if (data.optionValue) {
                  const layoutUpdate: Record<string, unknown> = {
                    width: data.optionValue as 'centered' | 'full',
                  };
                  if (data.optionValue === 'centered') layoutUpdate.maxWidth = 1600;
                  onSettingsChange({ layout: layoutUpdate });
                }
              }}
              appearance="underline"
            >
              <Option value="centered" text="Centered (1600px)">
                Centered (1600px)
              </Option>
              <Option value="full" text="Full width">
                Full width
              </Option>
            </Dropdown>
          </div>
        </div>
    </SidePanel>
  );
};

export default DashboardSettingsPanel;